import React, { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { useConfigurationContext } from "context/ConfigurationContext";
import MetricList from "modules/metric/conatiners/MetricList";
import MKGSubGraphVisualizer from "modules/mkg/containers/MKGSubGraphVisualizer";
import SplitView from "modules/common/SplitView";
import MetricDetailFormContainer from "../conatiners/MetricDetailForm";

// ---------------------------------------------------------------------------
// MetricDetailPanel
// ---------------------------------------------------------------------------

const MetricDetailPanel: React.FC<{ onReloadKnowledgeGraph: () => void }> = ({ onReloadKnowledgeGraph }) => {
  const {
    selectedMetric,
    setSelectedMetric,
    editMetricDefinition,
    setEditMetricDefinition,
    viewMKG,
    setViewMKG,
    handleEditMetricDefinitionModal,
    handleViewMKGModal,
    deleteMetricById,
  } = useConfigurationContext();
  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const closePanel = useCallback(() => {
    setEditMetricDefinition(false);
    setViewMKG(false);
    setSelectedMetric(null);
  }, [setEditMetricDefinition, setViewMKG, setSelectedMetric]);

  if (!selectedMetric) {
    return (
      <Center h="full" flexDirection="column" gap={3} color="gray.400">
        <Icon as={AppIcons.metric} boxSize={10} />
        <Text fontSize="sm" mb={0}>
          Select a metric to edit its definition
        </Text>
      </Center>
    );
  }

  const isSubgraph = viewMKG && !editMetricDefinition;

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    await deleteMetricById(selectedMetric.id!);
  };

  return (
    <>
      <Flex direction="column" h="full">
        {/* Header: title | tab switcher | actions */}
        <Flex
          px={4}
          py={2.5}
          borderBottomWidth="1px"
          borderColor="gray.100"
          justify="space-between"
          align="center"
          flexShrink={0}
          bg="white"
          gap={3}
        >
          <Text
            fontWeight="700"
            color="gray.900"
            fontSize="md"
            noOfLines={2}
            mb={0}
            lineHeight="1.35"
            textAlign="left"
            flex={1}
            minW={0}
          >
            {selectedMetric.name}
          </Text>

          {/* Tab switcher */}
          <Flex bg="gray.100" borderRadius="lg" p={0.5} gap={0.5} flexShrink={0}>
            <Button
              size="xs"
              borderRadius="md"
              variant="ghost"
              fontWeight={!isSubgraph ? "700" : "500"}
              bg={!isSubgraph ? "white" : "transparent"}
              color={!isSubgraph ? "purple.700" : "gray.500"}
              boxShadow={!isSubgraph ? "sm" : "none"}
              _hover={{ bg: !isSubgraph ? "white" : "gray.200" }}
              onClick={() => handleEditMetricDefinitionModal(selectedMetric)}
              px={3}
            >
              Definition
            </Button>
            <Button
              size="xs"
              borderRadius="md"
              variant="ghost"
              fontWeight={isSubgraph ? "700" : "500"}
              bg={isSubgraph ? "white" : "transparent"}
              color={isSubgraph ? "purple.700" : "gray.500"}
              boxShadow={isSubgraph ? "sm" : "none"}
              _hover={{ bg: isSubgraph ? "white" : "gray.200" }}
              onClick={() => handleViewMKGModal(selectedMetric)}
              px={3}
            >
              Subgraph
            </Button>
          </Flex>

          <Flex gap={1} flexShrink={0} align="center">
            <Tooltip label="Delete metric" placement="top">
              <Button
                aria-label="Delete metric"
                leftIcon={<Icon as={AppIcons.delete} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                fontWeight="500"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            </Tooltip>
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<Icon as={AppIcons.checkCircle} />}
              onClick={() => setTriggerSubmit(true)}
              isDisabled={isSubgraph}
              opacity={isSubgraph ? 0 : 1}
              pointerEvents={isSubgraph ? "none" : "auto"}
            >
              Save
            </Button>
            <Tooltip label="Close panel" placement="top">
              <IconButton
                aria-label="Close panel"
                icon={<Icon as={AppIcons.cancelX} boxSize={5} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={closePanel}
              />
            </Tooltip>
          </Flex>
        </Flex>

        {/* Content */}
        <Box flex={1} overflow="hidden">
          {isSubgraph ? (
            <MKGSubGraphVisualizer metricId={selectedMetric} />
          ) : (
            <Box h="full" overflowY="auto">
              <MetricDetailFormContainer
                createMetricFlag={false}
                modelInfo={null}
                metricHeader={selectedMetric}
                onClose={closePanel}
                triggerSubmit={triggerSubmit}
                setTriggerSubmit={setTriggerSubmit}
                onReloadKnowledgeGraph={onReloadKnowledgeGraph}
              />
            </Box>
          )}
        </Box>
      </Flex>

      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Metric
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <strong>{selectedMetric.name}</strong>? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// MetricsView
// ---------------------------------------------------------------------------

export interface MetricsViewProps {
  onReloadKnowledgeGraph: () => void;
}

const MetricsView: React.FC<MetricsViewProps> = ({ onReloadKnowledgeGraph }) => {
  const { selectedMetric, editMetricDefinition, viewMKG } = useConfigurationContext();
  const metricsDetailOpen = (editMetricDefinition || viewMKG) && selectedMetric !== null;

  return (
    <SplitView
      leftPanel={<MetricList onReloadKnowledgeGraph={onReloadKnowledgeGraph} splitDetailOpen={metricsDetailOpen} />}
      rightPanel={<MetricDetailPanel onReloadKnowledgeGraph={onReloadKnowledgeGraph} />}
      isRightPanelVisible={metricsDetailOpen}
      defaultRightWidth={35}
    />
  );
};

export default MetricsView;
